import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { addDays, format, isSameDay, startOfWeek } from "date-fns";
import { vi } from "date-fns/locale";
import { ChevronLeft, ChevronRight, CalendarX, MapPin, BookOpen } from "lucide-react";
import { PortalLayout } from "@/components/portal-layout";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/student/schedule")({ component: SchedulePage });

const SUBJECT_PALETTE = [
  "from-blue-500 to-blue-600",
  "from-emerald-500 to-emerald-600",
  "from-amber-500 to-amber-600",
  "from-violet-500 to-violet-600",
  "from-pink-500 to-pink-600",
  "from-cyan-500 to-cyan-600",
];

function colorFor(key: string) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return SUBJECT_PALETTE[Math.abs(h) % SUBJECT_PALETTE.length];
}

function SchedulePage() {
  const { user } = useAuth();
  const [weekOffset, setWeekOffset] = React.useState(0);
  const [filterClass, setFilterClass] = React.useState("all");

  const monday = React.useMemo(() => {
    const base = startOfWeek(new Date(), { weekStartsOn: 1 });
    return addDays(base, weekOffset * 7);
  }, [weekOffset]);
  const sunday = addDays(monday, 6);

  const q = useQuery({
    queryKey: ["student-schedule", user?.id, monday.toISOString()],
    enabled: !!user,
    queryFn: async () => {
      const { data: enrolls } = await supabase
        .from("enrollments").select("class_id").eq("student_id", user!.id).eq("is_active", true);
      const ids = (enrolls ?? []).map((e) => e.class_id);
      if (!ids.length) return [];
      const from = monday.toISOString();
      const to = new Date(sunday).setHours(23, 59, 59, 999);
      const [sessRes, classRes] = await Promise.all([
        supabase.from("class_sessions")
          .select("id, class_id, starts_at, ends_at, room, topic")
          .in("class_id", ids)
          .gte("starts_at", from)
          .lte("starts_at", new Date(to).toISOString())
          .order("starts_at"),
        supabase.from("classes").select("id, name, subject").in("id", ids),
      ]);
      if (sessRes.error) throw sessRes.error;
      const cm = new Map((classRes.data ?? []).map((c) => [c.id, c]));
      return (sessRes.data ?? []).map((s) => ({ ...s, topic: (s as unknown as { topic: string | null }).topic ?? null, class: cm.get(s.class_id) }));
    },
  });

  const classOptions = React.useMemo(() => {
    const map = new Map<string, string>();
    (q.data ?? []).forEach(s => { if (s.class) map.set(s.class_id, s.class.name); });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [q.data]);

  const filteredSessions = React.useMemo(() => {
    if (filterClass === "all") return q.data ?? [];
    return (q.data ?? []).filter(s => s.class_id === filterClass);
  }, [q.data, filterClass]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const today = new Date();
  const total = filteredSessions.length;

  return (
    <PortalLayout
      portal="student" title="Lịch học của tôi"
      breadcrumb={[{ label: "Học sinh", to: "/student" }, { label: "Lịch học" }]}
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-display text-xl font-semibold">
            {format(monday, "dd/MM")} – {format(sunday, "dd/MM/yyyy")}
          </div>
          <div className="text-sm text-muted-foreground">
            {weekOffset === 0 ? "Tuần này" : weekOffset > 0 ? `${weekOffset} tuần tới` : `${Math.abs(weekOffset)} tuần trước`} · {total} buổi học
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Tất cả lớp" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tất cả lớp</SelectItem>
              {classOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="size-9" onClick={() => setWeekOffset((w) => w - 1)}>
            <ChevronLeft className="size-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWeekOffset(0)} disabled={weekOffset === 0}>
            Hôm nay
          </Button>
          <Button variant="outline" size="icon" className="size-9" onClick={() => setWeekOffset((w) => w + 1)}>
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      {total === 0 && !q.isLoading ? (
        <div className="rounded-2xl border-2 border-dashed bg-card p-16 text-center">
          <CalendarX className="size-12 mx-auto text-muted-foreground mb-3" />
          <div className="font-medium">Không có buổi học nào trong tuần</div>
          <div className="text-sm text-muted-foreground mt-1">
            Lịch học sẽ xuất hiện khi trung tâm xếp lịch cho lớp của bạn.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-3">
          {days.map((d, di) => {
            const slots = filteredSessions.filter((s) => isSameDay(new Date(s.starts_at), d));
            const isToday = isSameDay(d, today);
            return (
              <motion.div
                key={di}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: di * 0.04 }}
                className={`bg-card border rounded-2xl p-3 min-h-[280px] transition-colors ${isToday ? "ring-2 ring-[var(--portal)] bg-[var(--portal-soft)]" : ""}`}
              >
                <div className="text-center pb-3 border-b mb-3">
                  <div className={`text-xs uppercase ${isToday ? "font-bold text-[var(--portal)]" : "text-muted-foreground"}`}>
                    {format(d, "EEE", { locale: vi })}
                  </div>
                  <div className={`font-display text-2xl mt-1 ${isToday ? "font-bold text-[var(--portal)]" : "font-semibold"}`}>
                    {format(d, "dd")}
                  </div>
                </div>
                <div className="space-y-2">
                  {slots.map((s) => {
                    const past = new Date(s.ends_at) < today;
                    const grad = colorFor(s.class?.subject ?? s.class?.name ?? s.class_id);
                    return (
                      <div
                        key={s.id}
                        className={`p-2.5 rounded-lg text-white text-xs shadow-sm bg-gradient-to-br ${grad} ${past ? "opacity-60" : ""}`}
                        title={`${s.class?.name ?? "Lớp"} · ${format(new Date(s.starts_at), "HH:mm")}–${format(new Date(s.ends_at), "HH:mm")}${s.room ? ` · ${s.room}` : ""}`}
                      >
                        <div className="font-semibold leading-tight">{s.class?.name ?? "Lớp"}</div>
                        <div className="opacity-90 mt-0.5">
                          {format(new Date(s.starts_at), "HH:mm")}–{format(new Date(s.ends_at), "HH:mm")}
                        </div>
                        {s.room && (
                          <div className="opacity-80 text-[10px] flex items-center gap-1 mt-0.5">
                            <MapPin className="size-2.5" /> {s.room}
                          </div>
                        )}
                        {s.topic && (
                          <div className="opacity-90 text-[10px] mt-0.5 truncate flex items-center gap-1">
                            <BookOpen className="size-2.5 shrink-0" /> {s.topic}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {slots.length === 0 && (
                    <div className="text-center text-xs text-muted-foreground pt-8">—</div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </PortalLayout>
  );
}
